import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { useMemo } from "react";

const formatChange = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};

const ADOPTION_ACTIVE_COLOR = "#6C2BFF";
const ADOPTION_ACTIVE_FILL = "#E9E2FF";
const ADOPTION_TOTAL_COLOR = "#A78BFA";
const ADOPTION_TOTAL_FILL = "#E0E7FF";
const ADOPTION_GRADIENT_START = "#C4B5FD";
const ADOPTION_GRADIENT_MID = "#A78BFA";
const ADOPTION_GRADIENT_END = "#7C3AED";

const TotalDoctorsTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="text-xs uppercase tracking-wide text-gray-400">{data.name}</p>
        <p className="text-sm font-semibold text-gray-700">
          Total Doctors: <span className="text-gray-900">{data.total}</span>
        </p>
        <p className="text-sm text-gray-600">
          Monthly Active: <span className="text-gray-900">{data.active}</span>
        </p>
      </div>
    );
  }
  return null;
};

const TotalsLegend = ({ payload }) => (
  <div className="w-full flex flex-col items-center justify-center gap-2 text-sm sm:flex-row sm:gap-6">
    {(payload || []).map((entry) => {
      const isTotal = entry?.dataKey === "total";
      const stroke = entry?.color || (isTotal ? ADOPTION_TOTAL_COLOR : ADOPTION_ACTIVE_COLOR);
      return (
        <div key={entry.dataKey} className="flex items-center gap-2 mt-1">
          <svg width="22" height="6" aria-hidden="true">
            <line
              x1="0"
              y1="3"
              x2="22"
              y2="3"
              stroke={stroke}
              strokeWidth="2"
              strokeDasharray={isTotal ? "6 4" : "0"}
            />
          </svg>
          <span style={{ color: stroke }}>{entry.value}</span>
        </div>
      );
    })}
  </div>
);

const ActivityTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="text-xs uppercase tracking-wide text-gray-400">{data.name}</p>
        <div className="mt-2 space-y-1 text-sm text-black">
          <p>
            Daily Activation: <span className="font-semibold text-black">{data.daily}</span>
          </p>
          <p>
            Weekly Avg: <span className="font-semibold text-black">{data.weekly_average}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const toNumber = (value) => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

const mapDailyActivations = (data) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  return data.map((entry, index) => {
    const previous = index > 0 ? data[index - 1] : null;
    const daily = toNumber(entry.daily);
    const weeklyAverage = toNumber(entry.weekly_average);
    const totalDoctors = toNumber(entry.total_doctors_cumulative);
    const prevDaily = previous ? toNumber(previous.daily) : null;
    const change = prevDaily !== null ? ((daily - prevDaily) / Math.max(prevDaily, 1)) * 100 : null;
    return {
      ...entry,
      daily,
      weekly_average: weeklyAverage,
      total_doctors_cumulative: totalDoctors,
      change,
    };
  });
};

const mapTotals = (data) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  return data.map((entry) => ({
    ...entry,
    total: toNumber(entry.total),
    active: toNumber(entry.active),
  }));
};

export default function RevOpsLineGraph({
  data,
  totalsData,
  showTotals = true,
  showActivity = true,
}) {
  const chartData = useMemo(() => mapDailyActivations(data), [data]);
  const totalsChartData = useMemo(() => mapTotals(totalsData), [totalsData]);
  const yMaxTop = useMemo(() => {
    if (!totalsChartData.length) return "auto";
    const maxValue = Math.max(...totalsChartData.map((entry) => entry.total ?? 0));
    return Math.ceil(maxValue * 1.02);
  }, [totalsChartData]);
  const yMaxBottom = useMemo(() => {
    if (!chartData.length) return "auto";
    const maxValue = Math.max(
      ...chartData.map((entry) => Math.max(entry.daily ?? 0, entry.weekly_average ?? 0))
    );
    return Math.ceil(maxValue * 1.05);
  }, [chartData]);
  const xTicks = useMemo(() => {
    if (!totalsChartData.length) return undefined;
    const indices = [0, 2, 5].filter((index) => index < totalsChartData.length);
    return indices.map((index) => totalsChartData[index]?.name).filter(Boolean);
  }, [totalsChartData]);
  const bottomTicks = useMemo(() => {
    if (!chartData.length) return undefined;
    const lastIndex = chartData.length - 1;
    const midIndex = Math.floor(lastIndex / 2);
    return [
      chartData[0]?.name,
      chartData[midIndex]?.name,
      chartData[lastIndex]?.name,
    ].filter(Boolean);
  }, [chartData]);

  if ((showActivity && !data) || (showTotals && !totalsData)) {
    const loadingHeightClass = showTotals && showActivity ? "h-[400px]" : "h-[280px]";
    return (
      <div className={`w-full ${loadingHeightClass} flex justify-center items-center`}>
        <div className="w-20 h-20 border-4 border-dashed rounded-full animate-spin-slow border-ktx-purple flex justify-center items-center"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className={`grid grid-cols-1 ${showTotals && showActivity ? "lg:grid-cols-2" : ""} gap-6`}>
        {showTotals && (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={totalsChartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="revopsActiveGradient" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor={ADOPTION_ACTIVE_COLOR} stopOpacity={1} />
                    <stop offset="45%" stopColor={ADOPTION_ACTIVE_COLOR} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={ADOPTION_ACTIVE_FILL} stopOpacity={1} />
                  </linearGradient>
                  <filter id="revopsLineGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="blur" />
                    <feColorMatrix
                      in="blur"
                      type="matrix"
                      values="
                        1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 0 0"
                      result="glow"
                    />
                    <feMerge>
                      <feMergeNode in="glow" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="name"
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  ticks={xTicks}
                  interval={0}
                  tickMargin={10}
                  height={40}
                  padding={{ left: 10, right: 20 }}
                />
                <YAxis
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, yMaxTop]}
                />
                <Tooltip content={<TotalDoctorsTooltip />} />
                <Legend
                  height={24}
                  wrapperStyle={{ paddingTop: 0, marginTop: 0, display: "flex", justifyContent: "center", width: "100%" }}
                  content={<TotalsLegend />}
                />
                <Area
                  type="monotone"
                  dataKey="active"
                  stroke={ADOPTION_ACTIVE_COLOR}
                  strokeWidth={3}
                  fill="url(#revopsActiveGradient)"
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 8, stroke: ADOPTION_ACTIVE_COLOR, strokeWidth: 2 }}
                  name="Monthly Active Doctors"
                  filter="url(#revopsLineGlow)"
                  strokeLinecap="round"
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke={ADOPTION_TOTAL_COLOR}
                  strokeWidth={3}
                  strokeDasharray="8 5"
                  fill={ADOPTION_TOTAL_FILL}
                  fillOpacity={0.5}
                  dot={false}
                  activeDot={{ r: 8, stroke: ADOPTION_TOTAL_COLOR, strokeWidth: 2 }}
                  name="Total Doctors"
                  strokeLinecap="round"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {showActivity && (
          <div className="flex flex-col gap-2">
            <div className="text-xs uppercase tracking-wide text-slate-500">Current Month</div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="name"
                    stroke="#666"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    ticks={bottomTicks}
                    interval={0}
                    tickMargin={10}
                    height={40}
                    padding={{ left: 10, right: 20 }}
                  />
                  <YAxis
                    stroke="#666"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, yMaxBottom]}
                  />
                  <Tooltip content={<ActivityTooltip />} />
                  <Legend
                    height={24}
                    wrapperStyle={{ paddingTop: 0, marginTop: 0 }}
                    iconType="square"
                    formatter={(value) => <span className="text-black">{value}</span>}
                  />
                  <Bar dataKey="daily" fill="#C4B5FD" radius={[4, 4, 0, 0]} name="Daily Activation" />
                  <Line
                    type="monotone"
                    dataKey="weekly_average"
                    stroke="#A78BFA"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4, stroke: "#A78BFA", strokeWidth: 2 }}
                    name="Weekly Avg"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
